import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    CircularProgress,
    IconButton,
    Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyIcon from '@mui/icons-material/Key';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { getStebbyApiKey, updateStebbyApiKey, deleteStebbyApiKey } from '../api/stebbyApi';

const StebbyApiModal = ({ open, onClose, affiliateId, affiliateName }) => {
    const [stebbyId, setStebbyId] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingInitial, setLoadingInitial] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [hasExistingKey, setHasExistingKey] = useState(false);

    // Load existing API key when modal opens
    useEffect(() => {
        if (open && affiliateId) {
            loadExistingApiKey();
        }
    }, [open, affiliateId]);

    const loadExistingApiKey = async () => {
        setLoadingInitial(true);
        setError('');
        try {
            const response = await getStebbyApiKey(affiliateId);
            if (response.success) {
                setHasExistingKey(response.hasApiKey);
                setStebbyId('');
            }
        } catch (error) {
            setError('Error loading existing API key');
        } finally {
            setLoadingInitial(false);
        }
    };

    // Reset state when modal closes
    const handleClose = () => {
        setStebbyId('');
        setError('');
        setSuccess('');
        setHasExistingKey(false);
        onClose();
    };

    // Save API key
    const handleSave = async () => {
        setError('');
        setSuccess('');

        if (!stebbyId.trim()) {
            setError('Please enter a Stebby API key');
            return;
        }

        setLoading(true);

        try {
            const response = await updateStebbyApiKey(affiliateId, stebbyId.trim());

            if (response.success) {
                setSuccess('Stebby API key saved successfully!');
                setHasExistingKey(true);

                // Auto-close modal after 2 seconds
                setTimeout(() => {
                    handleClose();
                }, 2000);
            } else {
                setError(response.message || 'Failed to save API key');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error saving API key');
        } finally {
            setLoading(false);
        }
    };

    // Delete API key
    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete the Stebby API key? This will disable Stebby integration for this affiliate.')) {
            return;
        }

        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const response = await deleteStebbyApiKey(affiliateId);

            if (response.success) {
                setSuccess('Stebby API key deleted successfully!');
                setHasExistingKey(false);
                setStebbyId('');

                // Auto-close modal after 2 seconds
                setTimeout(() => {
                    handleClose();
                }, 2000);
            } else {
                setError(response.message || 'Failed to delete API key');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error deleting API key');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <KeyIcon color="primary" />
                        <Typography variant="h6">Stebby API Configuration</Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {affiliateName}
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pb: 2 }}>
                {loadingInitial ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={40} />
                    </Box>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Enter a Stebby API key to enable or replace ticket redemption integration for this affiliate.
                        </Typography>

                        {/* Guide button */}
                        <Box sx={{ mb: 2 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<HelpOutlineIcon />}
                                onClick={() => {
                                    window.open('/stebby-guide', '_blank');
                                }}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontSize: '0.875rem'
                                }}
                            >
                                Setup Guide
                            </Button>
                        </Box>

                        {hasExistingKey && (
                            <Box sx={{ mb: 2 }}>
                                <Chip
                                    label="API Key Configured"
                                    color="success"
                                    size="small"
                                    icon={<KeyIcon />}
                                />
                            </Box>
                        )}

                        <TextField
                            fullWidth
                            label="Stebby API Key"
                            value={stebbyId}
                            onChange={(e) => setStebbyId(e.target.value)}
                            disabled={loading}
                            placeholder={hasExistingKey ? 'Enter a new key to replace the existing one' : 'Enter your Stebby API key'}
                            InputProps={{
                                startAdornment: <KeyIcon sx={{ mr: 1, color: 'action.active' }} />
                            }}
                            helperText={hasExistingKey ? 'For security, the existing key is hidden and cannot be viewed.' : 'You can find this key in your Stebby admin panel'}
                        />

                        {/* Success message */}
                        {success && (
                            <Alert severity="success" sx={{ mt: 2 }}>
                                {success}
                            </Alert>
                        )}

                        {/* Error message */}
                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                {!loadingInitial && (
                    <>
                        <Button onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>

                        {hasExistingKey && (
                            <Button
                                onClick={handleDelete}
                                color="error"
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
                            >
                                Delete
                            </Button>
                        )}

                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={loading || !stebbyId.trim()}
                            startIcon={loading ? <CircularProgress size={16} /> : <KeyIcon />}
                        >
                            {hasExistingKey ? 'Update' : 'Save'} API Key
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default StebbyApiModal;